/**
 * lib/appOpen.ts — reliable `parent_app_opened` (docs/plans/analytics-implementation-plan.md §6.2, §7.1).
 *
 * THE HIGHEST-RISK PIECE OF THIS PHASE. A backgrounded Capacitor app is not
 * relaunched on resume: `main.tsx` runs once at cold start and may not run
 * again for days. A `parent_app_opened` fired only from module scope
 * therefore massively undercounts native returns — and native is the primary
 * surface — so every parent retention/DAU figure (Q4) would be wrong in a way
 * that looks like a product problem rather than a measurement bug.
 *
 *   cold start                 → parent_app_opened { is_cold_start: true }
 *   foreground after ≥30s away → parent_app_opened { is_cold_start: false, seconds_backgrounded }
 *   foreground after  <30s     → SUPPRESSED (an incoming SMS during OTP entry
 *                                is not a return visit — see shouldCountResume)
 *   web                        → same semantics via `visibilitychange`
 *
 * Mirrors `apps/student-client/src/lib/appOpen.ts`. The decision logic itself
 * lives in `lib/analytics.ts` as a pure function so it can be unit tested
 * without mocking Capacitor.
 */

import { Capacitor } from '@capacitor/core';
import { capture, setSessionContext } from '@tupai/analytics/client';
import { markSessionOpened, readSessionContext, shouldCountResume } from './analytics';

/** Module-level, so a second call (React StrictMode, HMR) is a no-op. */
let started = false;
let backgroundedAt: number | null = null;

function refreshSessionContext(): void {
  setSessionContext(readSessionContext());
}

/**
 * Shared by both the Capacitor `appStateChange` listener and the web
 * `visibilitychange` handler — the two differ only in how they report
 * "is the app in front of the user right now".
 */
function handleActiveChange(isActive: boolean, now: number = Date.now()): void {
  if (!isActive) {
    backgroundedAt = now;
    return;
  }
  // Never backgrounded in this run (e.g. a spurious visible→visible) — the
  // cold-start event already covers it.
  if (backgroundedAt === null) return;
  const secondsBackgrounded = Math.round((now - backgroundedAt) / 1000);
  backgroundedAt = null;
  if (!shouldCountResume(secondsBackgrounded)) return;
  refreshSessionContext();
  capture('parent_app_opened', { is_cold_start: false, seconds_backgrounded: secondsBackgrounded });
}

/**
 * Fire the cold-start `parent_app_opened` and wire up resume tracking.
 * Fire-and-forget: never awaited, never throws into a caller.
 */
export function initAppOpen(): void {
  if (started) return;
  started = true;

  refreshSessionContext();
  capture('parent_app_opened', { is_cold_start: true });
  markSessionOpened();

  if (Capacitor.isNativePlatform()) {
    // Dynamic import so the web bundle never pulls the plugin in at all.
    import('@capacitor/app')
      .then(({ App }) =>
        App.addListener('appStateChange', ({ isActive }) => handleActiveChange(isActive))
      )
      .catch((err) => console.warn('[analytics] appStateChange listener failed:', err));
    return;
  }

  if (typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', () =>
    handleActiveChange(document.visibilityState === 'visible')
  );
}
